import washingIcon from '@assets/atemporal/fabrics/washing.png'
import washingSymbolsIcon from '@assets/atemporal/fabrics/washing-symbols.png'
import solventIcon from '@assets/atemporal/fabrics/solvent.png'
import ironIcon from '@assets/atemporal/fabrics/iron.png'
import noBleachIcon from '@assets/atemporal/fabrics/no-iron.png'
import dryCleanPIcon from '@assets/atemporal/fabrics/dry-clean.png'
import dryCleanIcon from '@assets/atemporal/fabrics/clothes.png'
import doNotTumbleDryIcon from '@assets/atemporal/fabrics/do-not-tumble-dry.png'
import dripDryIcon from '@assets/atemporal/fabrics/drip-dry.png'
import laundryIcon from '@assets/atemporal/fabrics/laundry.png'
import noWringIcon from '@assets/atemporal/fabrics/no-wring.png'
import { isEnglishUi, pickLocalizedText, type LocalizedText } from '@utils/i18n/uiLocale'
import { ui } from '@utils/i18n/uiCopy'

export type FabricCareIconId =
  | 'wash30'
  | 'washingSymbols'
  | 'noBleach'
  | 'iron'
  | 'noTumbleDry'
  | 'dryClean'
  | 'dryCleanP'
  | 'dripDry'
  | 'noWring'
  | 'gentleWash'
  | 'noDryClean'

export type FabricCharacteristicsProfile = {
  id: string
  title: string
  description: string
  icons: FabricCareIconId[]
  /** Grupo del panel (Algodón, Lino, …) cuando el campo `acabado` no es fiable. */
  group: string
}

const FABRIC_CARE_ICON_META: Record<
  FabricCareIconId,
  { src: string; alt: LocalizedText; sizeClass?: string; htmlSizeClass?: string }
> = {
  wash30: {
    src: washingIcon,
    alt: { es: 'Lavado a 30°C', en: 'Wash at 30°C' },
    sizeClass: 'h-9 w-9',
    htmlSizeClass: 'fabric-care-icon--medium',
  },
  washingSymbols: {
    src: washingSymbolsIcon,
    alt: { es: 'Símbolos de lavado', en: 'Washing symbols' },
    sizeClass: 'h-6 w-6',
    htmlSizeClass: 'fabric-care-icon--slightly-small',
  },
  noBleach: { src: noBleachIcon, alt: { es: 'No usar lejía', en: 'Do not bleach' } },
  iron: {
    src: ironIcon,
    alt: { es: 'Planchar a temperatura baja', en: 'Iron at low temperature' },
    sizeClass: 'h-8 w-8',
    htmlSizeClass: 'fabric-care-icon--slightly-large',
  },
  noTumbleDry: { src: doNotTumbleDryIcon, alt: { es: 'No secar en secadora', en: 'Do not tumble dry' } },
  dryClean: {
    src: dryCleanIcon,
    alt: { es: 'Lavado en seco', en: 'Dry clean' },
    sizeClass: 'h-8 w-8',
    htmlSizeClass: 'fabric-care-icon--slightly-large',
  },
  dryCleanP: { src: dryCleanPIcon, alt: { es: 'Lavado en seco (P)', en: 'Dry clean (P)' } },
  dripDry: { src: dripDryIcon, alt: { es: 'Secar en vertical', en: 'Drip dry' } },
  noWring: {
    src: noWringIcon,
    alt: { es: 'No retorcer', en: 'Do not wring' },
    sizeClass: 'h-8 w-8',
    htmlSizeClass: 'fabric-care-icon--large',
  },
  gentleWash: { src: laundryIcon, alt: { es: 'Lavado delicado', en: 'Gentle wash' } },
  noDryClean: {
    src: solventIcon,
    alt: { es: 'No lavar en seco', en: 'Do not dry clean' },
    sizeClass: 'h-6 w-6',
    htmlSizeClass: 'fabric-care-icon--slightly-small',
  },
}

/** Nombre visible de cada grupo del panel. Clave = normalizeFabricKey(acabado). */
const FABRIC_GROUP_LABELS: Record<string, LocalizedText> = {
  algodon: { es: 'Algodón', en: 'Cotton' },
  cotton: { es: 'Algodón', en: 'Cotton' },
  lino: { es: 'Lino', en: 'Linen' },
  linen: { es: 'Lino', en: 'Linen' },
  algodonylino: { es: 'Algodón y lino', en: 'Cotton and linen' },
  cottonandlinen: { es: 'Algodón y lino', en: 'Cotton and linen' },
  linoypoliester: { es: 'Lino y poliester', en: 'Linen and polyester' },
  linenandpolyester: { es: 'Lino y poliester', en: 'Linen and polyester' },
  poliester: { es: 'Poliéster', en: 'Polyester' },
  polyester: { es: 'Poliéster', en: 'Polyester' },
}

const FABRIC_CHARACTERISTICS_PROFILES: Array<{
  id: string
  match: (key: string) => boolean
  title: LocalizedText
  description: LocalizedText
  icons: FabricCareIconId[]
  groupKey: keyof typeof FABRIC_GROUP_LABELS
}> = [
  {
    id: 'mild-cotton',
    match: (key) => key.includes('mildcotton'),
    title: { es: 'Mild Cotton', en: 'Mild Cotton' },
    description: { es: '100% Algodón · Ancho 140 cms · Martindale 21.000', en: '100% Cotton · Width 140 cm · Martindale 21,000' },
    icons: ['iron', 'dryCleanP'],
    groupKey: 'algodon',
  },
  {
    id: '100-algodon-atemporal',
    match: (key) =>
      key.includes('100algodon') ||
      (key.includes('algodon') && key.includes('atemporal') && !key.includes('greatlinen')) ||
      (key.includes('atemporal') &&
        !key.includes('greatlinen') &&
        !key.includes('linen') &&
        !key.includes('mildcotton') &&
        !key.includes('belgium') &&
        !key.includes('linco') &&
        !key.includes('carlota') &&
        !key.includes('alessandra') &&
        !key.includes('clan') &&
        !key.includes('terra')),
    title: { es: '100% Algodón Atemporal', en: '100% Cotton Atemporal' },
    description: { es: '100% Algodón · Ancho 140 cms · Martindale 28.000 · Encogimiento 3/4%', en: '100% Cotton · Width 140 cm · Martindale 28,000 · Shrinkage 3/4%' },
    icons: ['wash30', 'noTumbleDry', 'iron', 'gentleWash', 'noDryClean', 'dripDry', 'dryClean'],
    groupKey: 'algodon',
  },
  {
    id: 'great-linen',
    match: (key) => key.includes('greatlinen'),
    title: { es: '100% Great Linen Atemporal', en: '100% Great Linen Atemporal' },
    description: { es: '100% Lino 750gr/m2 · Ancho 140 cms · Martindale 28.000 · Encogimiento 3/4%', en: '100% Linen 750gr/m2 · Width 140 cm · Martindale 28,000 · Shrinkage 3/4%' },
    icons: ['wash30', 'noTumbleDry', 'iron', 'gentleWash', 'noDryClean', 'dryClean'],
    groupKey: 'lino',
  },
  {
    id: 'carlota-soft',
    match: (key) => key.includes('carlota'),
    title: { es: 'Carlota SOFT', en: 'Carlota SOFT' },
    description: { es: '100% Lino · 750gr/m2 · Ancho 140 cms · Martindale 28.000 · Encogimiento 3/4%', en: '100% Linen · 750gr/m2 · Width 140 cm · Martindale 28,000 · Shrinkage 3/4%' },
    icons: ['wash30', 'noTumbleDry', 'iron', 'gentleWash', 'noWring', 'dryCleanP'],
    groupKey: 'lino',
  },
  {
    id: 'alessandra-easyclean',
    match: (key) => key.includes('alessandra'),
    title: { es: 'Alessandra EasyClean®', en: 'Alessandra EasyClean®' },
    description: { es: '100% Lino · 750gr/m2 · Ancho 140 cms · Martindale 22.500', en: '100% Linen · 750gr/m2 · Width 140 cm · Martindale 22,500' },
    icons: ['wash30', 'dripDry', 'noTumbleDry', 'iron', 'gentleWash', 'noDryClean', 'dryClean'],
    groupKey: 'lino',
  },
  {
    id: 'belgium',
    match: (key) => key.includes('belgium'),
    title: { es: 'Belgium', en: 'Belgium' },
    description: { es: '60% Lino 40% Algodón · 750gr/m2 · Ancho 140 cms · Martindale 25.000 · Encogimiento 0/1%', en: '60% Linen 40% Cotton · 750gr/m2 · Width 140 cm · Martindale 25,000 · Shrinkage 0/1%' },
    icons: ['dryCleanP'],
    groupKey: 'algodonylino',
  },
  {
    id: 'linco-easyclean',
    match: (key) =>
      (key.includes('linco') || key.includes('lincoeasyclean') || key.includes('lincoeasywash')) &&
      !key.includes('linenperformance') &&
      !(key.includes('linen') && key.includes('performance')),
    title: { es: 'Linco EasyClean®', en: 'Linco EasyClean®' },
    description: { es: '63% Lino 37% Algodón · Ancho 134 cms · Martindale 32.500', en: '63% Linen 37% Cotton · Width 134 cm · Martindale 32,500' },
    icons: ['wash30', 'dripDry', 'noTumbleDry', 'iron', 'gentleWash', 'noDryClean', 'dryClean'],
    groupKey: 'algodonylino',
  },
  {
    id: 'linen-performance-easyclean',
    match: (key) =>
      key.includes('linenperformance') || (key.includes('linen') && key.includes('performance')),
    title: { es: 'Linen Performance EasyClean®', en: 'Linen Performance EasyClean®' },
    description: { es: '93% Pes 7% Lino · Ancho 141 cms · Martindale 100.000', en: '93% Pes 7% Linen · Width 141 cm · Martindale 100,000' },
    icons: ['wash30', 'washingSymbols', 'iron', 'gentleWash', 'noDryClean'],
    groupKey: 'linoypoliester',
  },
  {
    id: 'clan',
    match: (key) => key.includes('clan'),
    title: { es: 'Clan', en: 'Clan' },
    description: { es: '63% Lino 37% Algodón · Ancho 136 cms · Martindale 32.500', en: '63% Linen 37% Cotton · Width 136 cm · Martindale 32,500' },
    icons: ['wash30', 'dripDry', 'iron', 'gentleWash', 'noDryClean', 'dryClean'],
    groupKey: 'algodonylino',
  },
  {
    id: 'terra',
    match: (key) => key.includes('terra'),
    title: { es: 'Terra', en: 'Terra' },
    description: { es: '100% Pes · Ancho 138 cms · Martindale 100.000', en: '100% Pes · Width 138 cm · Martindale 100,000' },
    icons: ['wash30', 'washingSymbols', 'noBleach', 'gentleWash', 'noDryClean'],
    groupKey: 'poliester',
  },
]

export function normalizeFabricKey(value?: string): string {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase()
}

/** Nombres de zona GLB que a veces llegan en el campo `acabado` en vez del grupo real. */
const GENERIC_FABRIC_GROUP_KEYS = new Set(['acabado', 'modelo', 'zona', 'tela', 'acabadomodelo'])

/** Nombre de fichero de API, p. ej. «1002 _Atemporal 11502». */
function looksLikeTextureFileCode(raw?: string): boolean {
  return /^\d{3,5}\s*_/.test(String(raw || '').trim())
}

function sanitizeFabricName(raw?: string): string {
  const value = String(raw || '').trim()
  if (!value) return ''
  const stripped = value.replace(/^\d{3,5}\s*_/, '').trim()
  return stripped || value
}

export function isGenericFabricGroupLabel(raw?: string): boolean {
  const key = normalizeFabricKey(raw).replace(/^\d+/, '')
  return !key || GENERIC_FABRIC_GROUP_KEYS.has(key)
}

export function formatFabricGroupLabel(raw?: string): string {
  const value = String(raw || '').trim()
  if (!value || isGenericFabricGroupLabel(value) || looksLikeTextureFileCode(value)) return ''
  const key = normalizeFabricKey(value).replace(/^\d+/, '')
  const label = FABRIC_GROUP_LABELS[key]
  if (label) return pickLocalizedText(label)
  return toGroupTitleCase(value)
}

/** Evita usar la descripción de zona («Acabado», «Tela») como grupo de tejido. */
export function fabricAcabadoFromApi(acabado?: string, descripcion?: string): string {
  const fromAcabado = String(acabado || '').trim()
  if (fromAcabado && !isGenericFabricGroupLabel(fromAcabado)) return fromAcabado
  const fromDescripcion = String(descripcion || '').trim()
  if (fromDescripcion && !isGenericFabricGroupLabel(fromDescripcion)) return fromDescripcion
  return ''
}

function hasKnownFabricGroup(raw?: string): boolean {
  const key = normalizeFabricKey(raw).replace(/^\d+/, '')
  return Boolean(key && FABRIC_GROUP_LABELS[key])
}

export function isKnownFabricGroupLabel(raw?: string): boolean {
  return hasKnownFabricGroup(raw)
}

function textureZoneLabel(texture: any): string {
  return String(texture?.zoneDisplayName || texture?.zone || texture?.zoneName || '').toLowerCase()
}

function textureFamilyName(texture: any): string {
  return String(texture?.name || texture?.descripcion || texture?.description || '').trim()
}

/**
 * Textura de tejido que deben leer chip, presupuesto y cabecera.
 * Preferimos la zona Tela; si el GLB Relax solo trae «Acabado», usamos
 * la que tenga un grupo/familia reales en vez de la etiqueta de zona.
 */
export function pickSelectedFabricTexture(textures: any[] | null | undefined): any | null {
  if (!Array.isArray(textures) || textures.length === 0) return null

  const byTela = textures.find((texture) => textureZoneLabel(texture).includes('tela'))
  if (
    byTela &&
    (hasKnownFabricGroup(byTela.acabado) || !isGenericFabricGroupLabel(textureFamilyName(byTela)))
  ) {
    return byTela
  }

  const withKnownGroup = textures.find((texture) => hasKnownFabricGroup(texture?.acabado))
  if (withKnownGroup) return withKnownGroup

  const withFamilyName = textures.find((texture) => {
    const name = textureFamilyName(texture)
    return Boolean(name) && !isGenericFabricGroupLabel(name)
  })
  if (withFamilyName) return withFamilyName

  return byTela || textures[0]
}

export function resolveFabricCharacteristics(
  acabado: string,
  fabricName = '',
): FabricCharacteristicsProfile | null {
  const key = normalizeFabricKey(`${acabado} ${fabricName}`.trim())
  if (!key) return null
  const profile = FABRIC_CHARACTERISTICS_PROFILES.find((entry) => entry.match(key))
  if (!profile) return null
  return {
    id: profile.id,
    title: pickLocalizedText(profile.title),
    description: pickLocalizedText(profile.description),
    icons: profile.icons,
    group: pickLocalizedText(FABRIC_GROUP_LABELS[profile.groupKey]),
  }
}

export type FabricTextureInput = {
  acabado?: string | null
  name?: string | null
  description?: string | null
  descripcion?: string | null
  modelo?: string | null
}

export type FabricPanelLabels = {
  group: string | null
  subgroup: string | null
  textureLabel: string | null
  profile: FabricCharacteristicsProfile | null
}

/** "ALGODÓN" → "Algodón"; "MILD COTTON" → "Mild cotton".
 * Solo reescribe si el texto llega mayoritariamente en mayúsculas (dump de API);
 * respeta casing ya correcto ("Mild Cotton", "Algodón").
 */
function toGroupTitleCase(raw: string) {
  const text = String(raw || '').trim()
  if (!text) return ''

  const letters = Array.from(text).filter((ch) => /\p{L}/u.test(ch))
  if (letters.length === 0) return text

  const upperCount = letters.filter(
    (ch) => ch === ch.toLocaleUpperCase('es') && ch !== ch.toLocaleLowerCase('es'),
  ).length
  const mostlyUpper = upperCount / letters.length >= 0.7
  if (!mostlyUpper) return text

  const lower = text.toLocaleLowerCase('es')
  return lower.charAt(0).toLocaleUpperCase('es') + lower.slice(1)
}

/**
 * Nombre visible de un tejido/acabado (panel y presupuesto).
 * Si hay perfil, usa su `title`. Conserva el código numérico final.
 */
export function localizeFabricDisplayName(raw?: string): string {
  const value = String(raw || '').trim()
  if (!value || isGenericFabricGroupLabel(value)) return ''

  const displayValue = looksLikeTextureFileCode(value) ? sanitizeFabricName(value) : value
  if (!displayValue || isGenericFabricGroupLabel(displayValue)) return ''

  const codeMatch = displayValue.match(/^(.*?)(\s+\d+)$/)
  const base = (codeMatch ? codeMatch[1] : displayValue).trim()
  const codeSuffix = codeMatch ? codeMatch[2] : ''
  const profile = resolveFabricCharacteristics(base)
  if (profile) return `${profile.title}${codeSuffix}`

  const titled = toGroupTitleCase(displayValue)
  if (!isEnglishUi()) return titled

  return `${base
    .replace(/\bAlgodón\b/gi, 'Cotton')
    .replace(/\bAlgodon\b/gi, 'Cotton')
    .replace(/\bLino\b/gi, 'Linen')
    .replace(/\bPoliéster\b/gi, 'Polyester')
    .replace(/\bPoliester\b/gi, 'Polyester')}${codeSuffix}`
}

export function resolveFabricTextureLabel(fabricTexture: FabricTextureInput | null) {
  const raw = String(
    fabricTexture?.description || fabricTexture?.descripcion || fabricTexture?.name || '',
  ).trim()
  const codes = raw.match(/\d{3,6}/g)
  return codes?.[codes.length - 1] || null
}

/** Grupo del panel + familia del tejido + código de color. */
export function resolveFabricPanelLabels(
  fabricTexture: FabricTextureInput | null,
): FabricPanelLabels {
  if (!fabricTexture) {
    return { group: null, subgroup: null, textureLabel: null, profile: null }
  }

  const acabado = String(fabricTexture.acabado || '').trim()
  const rawName = String(
    fabricTexture.name ||
      fabricTexture.descripcion ||
      fabricTexture.description ||
      fabricTexture.modelo ||
      '',
  ).trim()
  const description = String(fabricTexture.descripcion || fabricTexture.description || '').trim()
  const name = looksLikeTextureFileCode(rawName)
    ? description && !looksLikeTextureFileCode(description)
      ? description
      : sanitizeFabricName(rawName)
    : rawName
  const profile = resolveFabricCharacteristics(acabado, name)
  const group = formatFabricGroupLabel(acabado) || profile?.group || null
  const subgroup = profile?.title || localizeFabricDisplayName(name) || null

  return {
    group,
    subgroup: group && subgroup === group ? null : subgroup,
    textureLabel: resolveFabricTextureLabel(fabricTexture),
    profile,
  }
}

export function getFabricCareIconMeta(iconId: FabricCareIconId) {
  const icon = FABRIC_CARE_ICON_META[iconId]
  return { ...icon, alt: pickLocalizedText(icon.alt) }
}

export function getFabricCareIconSrc(iconId: FabricCareIconId): string {
  return FABRIC_CARE_ICON_META[iconId]?.src || ''
}

export function getFabricCareIconClassName(iconId: FabricCareIconId): string {
  return FABRIC_CARE_ICON_META[iconId]?.sizeClass || 'h-7 w-7'
}

export function getFabricCareIconHtmlClassName(iconId: FabricCareIconId): string {
  const extra = FABRIC_CARE_ICON_META[iconId]?.htmlSizeClass
  return extra ? `fabric-care-icon ${extra}` : 'fabric-care-icon'
}

async function moduleUrlToDataUrl(url: string): Promise<string> {
  if (!url) return ''
  try {
    const response = await fetch(url)
    const blob = await response.blob()
    return await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        resolve(typeof reader.result === 'string' ? reader.result : '')
      }
      reader.onerror = () => reject(new Error('read'))
      reader.readAsDataURL(blob)
    })
  } catch {
    return ''
  }
}

export async function fetchFabricCareIconsAsDataUrls(
  profile: FabricCharacteristicsProfile | null,
): Promise<Partial<Record<FabricCareIconId, string>>> {
  if (!profile) return {}

  const uniqueIcons = Array.from(new Set(profile.icons))
  const entries = await Promise.all(
    uniqueIcons.map(async (iconId) => {
      const src = getFabricCareIconSrc(iconId)
      const dataUrl = await moduleUrlToDataUrl(src)
      return [iconId, dataUrl] as const
    }),
  )

  return Object.fromEntries(entries.filter(([, dataUrl]) => Boolean(dataUrl)))
}

export function buildFabricCharacteristicsHtml(
  escapeHtml: (value: string) => string,
  profile: FabricCharacteristicsProfile,
  iconDataUrls: Partial<Record<FabricCareIconId, string>>,
): string {
  const iconsHtml = profile.icons
    .map((iconId) => {
      const src = iconDataUrls[iconId] || getFabricCareIconSrc(iconId)
      const alt = escapeHtml(getFabricCareIconMeta(iconId).alt)
      if (!src) return ''
      return `<img class="${getFabricCareIconHtmlClassName(iconId)}" src="${src}" alt="${alt}" />`
    })
    .filter(Boolean)
    .join('')

  return `<div class="fabric-care-block">
    <div class="fabric-care-title"><span class="label-strong">${escapeHtml(ui('fabricCharacteristics'))}</span></div>
    <div class="fabric-care-group">${escapeHtml(profile.title)}</div>
    <div class="fabric-care-desc">${escapeHtml(profile.description)}</div>
    <div class="fabric-care-icons">${iconsHtml}</div>
  </div>`
}
